import "../../../css/GlobalStyles.css";
import {
  AccountData,
  Conversation,
  Follower,
  FollowerMembershipState,
  FollowerStatus,
  IMessage,
  TextTypes,
  TwilioMessageStatus,
} from "@markit/common.types";
import { getAccountSlice } from "../../../redux/slices/accountSlice";
import { onSnapshot } from "../../../firebase";
import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { API } from "../../../API";
import {
  getUserConversationMessageRef,
  getUserConversationRef,
} from "../../../utils/FirebaseUtils";
import { Colors } from "../../../utils/colors";
import TextareaAutosize from "react-textarea-autosize";
import ProfilePic from "../../ProfilePic";
import { uniqueVals } from "@markit/common.utils";
import { useOnMount } from "../../../hooks/useOnMount";
import filter from "lodash.filter";
import ConversationsMessagesContainer from "./ConversationsMessagesContainer";
import { CircularProgress } from "@mui/material";
import ConversationMessageInput from "./ConversationMessageInput";
import FullProfilePreviewModal from "../../FollowerProfile/FullProfilePreviewModal";
import useAsyncEffect from "../../../hooks/useAsyncEffect";
import { updateChatConversationIndicator } from "../../../redux/slices/conversationSlice";
import ConversationItemDropdownSettings from "./ConversationItemDropdownSettings";
import { Icon } from "@iconify/react";
import EmptyStateButton from "../../Buttons/EmptyStateButton";
import { useLoadUserFollowList } from "../../../hooks/useLoadUserFollowList";

type ConversationsMainViewProps = {
  conversationUser: AccountData | undefined;
  setConversationUser: (conversationUser: AccountData | undefined) => void;
  fetchedConversations: Conversation[];
  fetchedConversationUserData: AccountData[];
  fetchedFollowerData: Follower | undefined;
  newUserSearchTerm: string;
  setNewUserSearchTerm: (newUserSearchTerm: string) => void;
  setNewConversation: (newConversation: boolean) => void;
  fetchedFollowers: AccountData[];
  loadSearchResultsThrottled: (text: string) => void;
  addTextPreset: () => void;
  onLocalConversationUpdate: (conversation: Conversation) => void;
};

const ConversationsMainView = (props: ConversationsMainViewProps) => {
  const {
    conversationUser,
    setConversationUser,
    fetchedConversations,
    fetchedConversationUserData,
    fetchedFollowerData,
    newUserSearchTerm,
    setNewConversation,
    setNewUserSearchTerm,
    fetchedFollowers,
    loadSearchResultsThrottled,
    addTextPreset,
    onLocalConversationUpdate,
  } = props;
  const dispatch = useDispatch();
  const { accountData } = useSelector(getAccountSlice);
  const [messageCount, setMessageCount] = useState(15);
  const [fetchedAllMessages, setFetchedAllMessages] = useState(false);
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [loadingConversation, setLoadingConversation] = useState<boolean>(true);
  const [loadingMoreMessages, setLoadingMoreMessages] =
    useState<boolean>(false);
  const [showLoadingState, setShowLoadingState] = useState(false);
  const [profilePreviewSelected, setProfilePreviewSelected] =
    useState<AccountData>();
  const [isOpenDropdown, setIsOpenDropdown] = useState<boolean>(false);

  const scrollableFeedRef = useRef<any>(null);

  const scrollToBottom = useCallback(() => {
    scrollableFeedRef.current?.scrollToBottom();
  }, []);

  const {
    fetchedUserData: fetchedSearchedUserData,
    loadSearchResultsThrottled: loadFollowerSearchResultsThrottled,
  } = useLoadUserFollowList({
    userId: accountData.uid,
    followListType: "Followers",
    followerStatus: FollowerStatus.SUBSCRIBED,
    windowSize: 5,
  });

  const selectedConversation = useMemo(
    () =>
      fetchedConversations.find(
        (convo) => conversationUser && convo.uid === conversationUser.uid
      ),
    [conversationUser, fetchedConversations]
  );

  const followerMembershipPlan = useMemo(
    () =>
      fetchedFollowerData &&
      fetchedFollowerData.membershipState === FollowerMembershipState.ACTIVE
        ? accountData.membership.membershipPlans.find(
            (plan) => plan.id === fetchedFollowerData.membershipPlanId
          )
        : undefined,
    [accountData.membership.membershipPlans, fetchedFollowerData]
  );

  const styles = {
    profileViewButton: {
      cursor: "pointer",
      height: 30,
      paddingInline: 14,
      borderRadius: 100,
      border: `0.5px solid ${Colors.GRAY2}`,
      backgroundColor: Colors.WHITE1,
      transition: "opacity 0.2s ease",
    },
  };

  const ref = useRef<any>(null);
  useEffect(() => {
    const handler = (event: any) => {
      if (
        ref.current &&
        !ref.current.contains(event.target) &&
        !profilePreviewSelected
      ) {
        setNewUserSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      // Cleanup the event listener
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [profilePreviewSelected, setNewUserSearchTerm]);

  const handleSearch = (text: string) => {
    const lowercaseText = text.toLowerCase();
    if (lowercaseText !== "") {
      loadSearchResultsThrottled(lowercaseText);
      loadFollowerSearchResultsThrottled(lowercaseText);
    }
    setNewUserSearchTerm(lowercaseText);
  };

  const containsUser = useCallback((item: AccountData, query: string) => {
    return (
      item.fullName.toLowerCase().includes(query) && item.phoneNumber !== ""
    );
  }, []);

  const followersToShow: AccountData[] = useMemo(() => {
    if (newUserSearchTerm === "") {
      return [];
    }
    let userList: AccountData[] = uniqueVals(
      fetchedFollowers.concat(fetchedSearchedUserData),
      (user) => user.uid
    );
    userList = filter(userList, (user: AccountData) => {
      return containsUser(user, newUserSearchTerm);
    });
    return userList.slice(0, 5);
  }, [
    newUserSearchTerm,
    fetchedFollowers,
    containsUser,
    fetchedSearchedUserData,
  ]);

  const fetchConversationMessages = useCallback(
    async (increment: boolean) => {
      if (selectedConversation && conversationUser) {
        setLoadingMoreMessages(true);
        const response = await API.creatorText.fetchUserConversationMessages({
          conversationId: selectedConversation.uid,
          twilioUser: accountData,
          attendeeUser: conversationUser,
          messageLimit: increment ? messageCount + 15 : messageCount,
        });
        const { messages } = response;
        if (messages.length === 0) {
          setFetchedAllMessages(true);
        }
        setNewConversation(false);
        setMessageCount(increment ? messageCount + 15 : messageCount);
        const sortedMessages = messages.sort((x: IMessage, y: IMessage) => {
          return (
            new Date(x.createdAt).getTime() - new Date(y.createdAt).getTime()
          );
        });
        setMessages(uniqueVals(sortedMessages));
        setLoadingMoreMessages(false);
      }
    },
    [
      accountData,
      conversationUser,
      messageCount,
      selectedConversation,
      setNewConversation,
    ]
  );

  const newIMessage = useCallback(
    (convoData: Conversation) => {
      const iMessage: IMessage = {
        _id: convoData.recentMessage.twilioMessageId,
        text: convoData.recentMessage.text ?? "",
        photoURL: convoData.recentMessage.photoURL ?? "",
        createdAt: new Date(convoData.recentTimestamp) ?? "",
        user: {
          _id: convoData.recentMessage.sentBy,
          name: fetchedConversationUserData.find(
            (m) => m.uid === convoData.recentMessage.sentBy
          )?.fullName,
          avatar: fetchedConversationUserData.find(
            (m) => m.uid === convoData.recentMessage.sentBy
          )?.profilePicURL,
        },
        type: TextTypes.CREATORMESSAGE,
        status: TwilioMessageStatus.INITIALIZED,
      };
      return iMessage;
    },
    [fetchedConversationUserData]
  );

  // Snapshot listener for any new messages added to the conversation thread
  useOnMount(() => {
    if (conversationUser) {
      const unsubscribe = onSnapshot(
        getUserConversationRef(accountData.uid, conversationUser.uid),
        (snapshot) => {
          const conversationData = snapshot.data();
          if (!conversationData) {
            return;
          }
          const iMessage: IMessage = newIMessage(conversationData);
          if (iMessage.text !== "" || iMessage.photoURL !== "") {
            setMessages((prev) => {
              if (conversationData.recentMessage.sentBy !== accountData.uid) {
                const index = prev.findIndex((c) => c._id === iMessage._id);
                if (index === -1) {
                  prev = prev.concat(iMessage);
                } else {
                  prev.splice(index, 1, iMessage);
                }
              } else {
                prev = prev.concat(iMessage);
              }
              return uniqueVals(prev, (message) => message._id.toString());
            });
          }
        }
      );
      return () => unsubscribe();
    }
  });

  // The latest message sent by the creator, if its delivery status hasn't settled yet
  const pendingSentMessageId = useMemo(() => {
    const latestSent = [...messages]
      .filter((message) => message.user._id === accountData.uid)
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )[0];
    return latestSent &&
      latestSent.status !== TwilioMessageStatus.DELIVERED &&
      latestSent.status !== TwilioMessageStatus.UNDELIVERED &&
      latestSent.status !== TwilioMessageStatus.FAILED
      ? String(latestSent._id)
      : undefined;
  }, [messages, accountData.uid]);

  // The delivery callback only updates the message doc (not the conversation doc), so listen to it
  // to show the Delivered / not delivered state live. Creators without conversation V2 writes have
  // no message doc, so nothing changes for them.
  useEffect(() => {
    if (!conversationUser || !pendingSentMessageId) {
      return;
    }
    const unsubscribe = onSnapshot(
      getUserConversationMessageRef(
        accountData.uid,
        conversationUser.uid,
        pendingSentMessageId
      ),
      (snapshot) => {
        const messageData = snapshot.data();
        if (!messageData) {
          return;
        }
        setMessages((prev) =>
          prev.map((message) =>
            String(message._id) === pendingSentMessageId &&
            message.status !== messageData.status
              ? { ...message, status: messageData.status }
              : message
          )
        );
      },
      () => {}
    );
    return () => unsubscribe();
  }, [accountData.uid, conversationUser, pendingSentMessageId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    if (conversationUser) {
      setLoadingConversation(true);
      const timeout = setTimeout(() => {
        // Show loading dots if converation messages take longer than three seconds to load
        if (loadingConversation) {
          setShowLoadingState(true);
        }
      }, 3000);
      await fetchConversationMessages(false);
      clearTimeout(timeout);
      setLoadingConversation(false);
      setShowLoadingState(false);
      return () => {
        if (timeout) {
          clearTimeout(timeout);
        }
      };
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationUser]);

  useEffect(() => {
    if (
      selectedConversation &&
      selectedConversation.chatIndicator.some(
        (member) => member.indicator && member.uid === accountData.uid
      )
    ) {
      onLocalConversationUpdate({
        ...selectedConversation,
        chatIndicator: selectedConversation.chatIndicator.map((member) =>
          member.uid === accountData.uid
            ? { ...member, indicator: false }
            : member
        ),
      });

      dispatch(
        updateChatConversationIndicator(
          selectedConversation.uid,
          accountData.uid,
          false
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversation?.uid]);

  const selectNewConversation = useCallback(
    async (item: AccountData) => {
      setMessages([]);
      setConversationUser(item);
      setNewUserSearchTerm("");
    },
    [setConversationUser, setNewUserSearchTerm]
  );

  const renderProfileViewButton = (item: AccountData, isFullView: boolean) => {
    return (
      <div
        className="Centering"
        style={styles.profileViewButton}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
        onClick={(e) => {
          setProfilePreviewSelected(item);
          e.stopPropagation();
        }}
      >
        <span className="bodyMedium">
          {isFullView ? "View Profile" : "View"}
        </span>
      </div>
    );
  };

  const renderLoadingConversation = useMemo(
    () =>
      showLoadingState ? (
        <div className="ColumnCentering" style={{ gap: 24, height: "100%" }}>
          <CircularProgress style={{ color: Colors.BLACK }} size={38} />
          <div
            className="ColumnNormal"
            style={{ gap: 14, textAlign: "center" }}
          >
            <span className="sectionTitle">Loading Chat</span>
            <span className="bodySubtext">This will only take a second</span>
          </div>
        </div>
      ) : (
        <div />
      ),
    [showLoadingState]
  );

  return (
    <div>
      {/* Conversation Header */}
      <div className="AlignedRow ConversationsMainViewHeader">
        {!conversationUser ? (
          <div
            ref={ref}
            className="AlignedRow"
            style={{ position: "relative", flexGrow: 1 }}
          >
            <div className="AlignedRow" style={{ flexGrow: 1 }}>
              <span className="sectionTitle">To:</span>
              <TextareaAutosize
                value={newUserSearchTerm}
                placeholder={""}
                onChange={(e) => handleSearch(e.target.value)}
                className="ConversationsInput bodyMedium"
                maxRows={1}
              />
            </div>
            {newUserSearchTerm.length > 1 || followersToShow.length > 0 ? (
              <div
                className="ColumnNormal"
                style={{
                  position: "absolute",
                  top: 50,
                  left: 30,
                  padding: 10,
                  borderRadius: 8,
                  minWidth: 340,
                  boxShadow: "0px 1px 6px 0px #b9b9b9",
                  backgroundColor: Colors.WHITE,
                }}
              >
                {followersToShow.length > 0 ? (
                  followersToShow.map((follower) => {
                    return (
                      <div
                        onClick={() => selectNewConversation(follower)}
                        className="AlignedRowSpacedSelect ConversationSidebarItem"
                        style={{ borderRadius: 8, paddingInline: 7 }}
                        key={follower.uid}
                      >
                        <div className="AlignedRow" style={{ gap: 10 }}>
                          <ProfilePic user={follower} size={40} />
                          <span
                            className="AboutSubtitle"
                            style={{ fontSize: 14 }}
                          >
                            {follower.fullName}
                          </span>
                        </div>
                        {renderProfileViewButton(follower, false)}
                      </div>
                    );
                  })
                ) : (
                  <EmptyStateButton
                    title="No Results"
                    description="Check for typos or search for something else"
                    icon={
                      <Icon
                        icon="ion:search"
                        height={30}
                        style={{ color: Colors.GRAY1 }}
                      />
                    }
                    iconBox={40}
                    containerStyles={{ paddingTop: 24 }}
                  />
                )}
              </div>
            ) : null}
          </div>
        ) : conversationUser ? (
          <div className="AlignedRowSpaced" style={{ flexGrow: 1 }}>
            <div className="AlignedRow" style={{ gap: 14 }}>
              <span className="sectionTitle">{conversationUser.fullName}</span>
              {followerMembershipPlan ? (
                <div className="AlignedRow" style={{ gap: 4 }}>
                  <Icon
                    icon="ion:ribbon"
                    height={12}
                    style={{ color: Colors.GRAY1 }}
                  />
                  <span className="smallBodySubtext">
                    {followerMembershipPlan.name}
                  </span>
                </div>
              ) : null}
            </div>
            <div className="AlignedRow" style={{ gap: 7 }}>
              {renderProfileViewButton(conversationUser, true)}
              {selectedConversation ? (
                <ConversationItemDropdownSettings
                  conversationItem={selectedConversation}
                  isOpenDropdown={isOpenDropdown}
                  setIsOpenDropdown={setIsOpenDropdown}
                  placement="bottomRight"
                  onLocalConversationUpdate={onLocalConversationUpdate}
                />
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
      {/* Conversation Messages */}
      <div
        className="ColumnNormal"
        style={{
          height: "calc(100vh - 280px)",
          justifyContent: "space-between",
        }}
      >
        {!loadingConversation ? (
          <ConversationsMessagesContainer
            userId={accountData.uid}
            messages={messages}
            conversationsMainUser={conversationUser}
            fetchedAllMessages={fetchedAllMessages}
            fetchConversationMessages={(increment: boolean) =>
              fetchConversationMessages(increment)
            }
            loadingMoreMessages={loadingMoreMessages}
            scrollableFeedRef={scrollableFeedRef}
          />
        ) : (
          renderLoadingConversation
        )}
        <div
          className="AlignedRowSpaced"
          style={{ marginTop: 10, marginBottom: 14 }}
        >
          <ConversationMessageInput
            conversationsMainUser={conversationUser}
            scrollToBottom={scrollToBottom}
            messages={messages}
            loadingConversation={loadingConversation}
            addTextPreset={addTextPreset}
          />
        </div>
      </div>
      {profilePreviewSelected ? (
        <FullProfilePreviewModal
          profileSelected={profilePreviewSelected}
          setProfileSelected={setProfilePreviewSelected}
        />
      ) : null}
    </div>
  );
};

export default ConversationsMainView;
